/**
 * 페이지 경계 계산 — 세로로 쌓이는 블록(차트 한 줄, 표 조각 등)이 페이지 경계에 걸리지 않게 앞에 여백을 둔다.
 * Table·Action Map 이 표 행으로 하던 것을 차트 줄에도 같은 규칙으로 쓴다 (2026-09-30 결정: 줄 단위 + 높이 자동).
 *
 * 좌표는 캔버스 기준 레이아웃 px. 격자 한 줄 = 30px + 사이 10px, 캔버스 안쪽 여백 20px.
 */
export const GRID_ROW_PX = 40
export const CANVAS_TOP_PX = 20
/** 페이지 첫머리·끝에 두는 여백 — 인쇄 여백처럼 보이게 */
export const PAGE_MARGIN_PX = 24

/**
 * 아이템 배치가 manual 인지 — manual 이면 페이지 경계를 보지 않고 그대로 그린다(사용자가 스페이서로 나눈다, 2026-10-01 결정).
 * Table·Action Map·Session Details 의 manual 은 "페이지마다 아이템 하나로 나눔"이라는 자기 뜻을 그대로 쓰므로 여기서 보지 않는다.
 */
export const isManualLayout = (options: { layoutMode?: "auto" | "manual" }) =>
  options.layoutMode === "manual"

/** 묶음 아이템이 페이지 끝 자투리에서 시작할 때, 이보다 작은 자투리면 다음 페이지 첫머리로 민다 */
export const MIN_PAGE_REMAINDER_PX = 160

/**
 * 묶음(Manual — 장·세션·보기마다 아이템 하나) 아이템의 위 여백 — 페이지 끝 자투리에서 시작하면 다음 페이지 첫머리까지 띄운다.
 * Table·Action Map·Session Details 가 같은 규칙을 쓴다 (2026-10-01 결정: 페이지 경계로 나눈다)
 */
export function groupedTopPadPx(itemTopPx: number, pageHeight: number): number {
  if (pageHeight <= 0) {
    return 0
  }
  const remainder = pageHeight - (itemTopPx % pageHeight)

  return remainder < MIN_PAGE_REMAINDER_PX ? remainder : 0
}

/**
 * 묶음의 마지막이 아닌 아이템이 차지할 격자 줄 수 — 페이지 끝까지 채워 다음 아이템이 다음 페이지 첫머리에서 시작하게 한다.
 */
export function fillToPageBottomRows(
  itemTopPx: number,
  padTopPx: number,
  pageHeight: number,
): number {
  if (pageHeight <= 0) {
    return 0
  }
  const contentTopPx = itemTopPx + padTopPx
  const pageBottomPx = (Math.floor(contentTopPx / pageHeight) + 1) * pageHeight

  return Math.floor((pageBottomPx - itemTopPx) / GRID_ROW_PX)
}

export interface PageBlockPlan {
  /** 블록마다 앞에 둘 빈 공간(px). 0 이면 바로 이어 붙는다 */
  padTops: number[]
  /** 여백을 포함한 전체 높이(px) */
  totalHeight: number
}

/**
 * 블록들을 topPx 부터 차례로 쌓되, 페이지 아래 여백을 넘게 되는 블록은 다음 페이지 첫머리(여백 뒤)로 민다.
 * 페이지 첫머리 여백 안에서 시작하면 여백 뒤로 민다(앞 아이템이 페이지 끝에 딱 붙어 끝났을 때).
 * 한 페이지에 들어가지 않을 만큼 큰 블록은 밀어도 소용없으므로 그 자리에 둔다.
 */
export function planPageBlocks(
  blockHeights: number[],
  topPx: number,
  pageHeight: number,
  marginPx = PAGE_MARGIN_PX,
): PageBlockPlan {
  const padTops: number[] = []
  if (pageHeight <= 0) {
    return {
      padTops: blockHeights.map(() => 0),
      totalHeight: blockHeights.reduce((sum, h) => sum + h, 0),
    }
  }
  let cursor = topPx
  blockHeights.forEach((blockHeight) => {
    let pad = 0
    let pageIndex = Math.floor(cursor / pageHeight)
    const intoPage = cursor - pageIndex * pageHeight
    if (intoPage < marginPx) {
      pad += marginPx - intoPage
      cursor += marginPx - intoPage
    }
    const pageBottom = (pageIndex + 1) * pageHeight - marginPx
    const fitsInAPage = blockHeight <= pageHeight - marginPx * 2
    if (fitsInAPage && cursor + blockHeight > pageBottom) {
      const nextTop = (pageIndex + 1) * pageHeight + marginPx
      pad += nextTop - cursor
      cursor = nextTop
      pageIndex += 1
    }
    padTops.push(pad)
    cursor += blockHeight
  })

  return { padTops, totalHeight: cursor - topPx }
}
